import { CheckCircle2, FileWarning, TriangleAlert } from 'lucide-react'

import { MoneyDisplay } from '@/components/money-display'
import { NotImplemented } from '@/components/not-implemented'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { resolveErrorMessage } from '@/lib/error-messages'
import type { Pesewas } from '@/lib/money'

import { COLUMN_SHORT_LABELS, type CollectionColumn } from './constants'
import {
  readAmount,
  type SheetAmounts,
  type SheetObjection,
  type SheetRow,
} from './sheet-model'
import type { LineResult, PreparedLine } from './use-batch-poster'

/**
 * Reconciling the paper against the system.
 *
 * Two of the three things the phrase covers are reachable on this contract and one is not, and the
 * panel is explicit about which is which.
 *
 * **Before the sheet posts** it compares what was keyed against what the system already holds, and
 * names the lines that will be refused, the members who owe money and have nothing against them,
 * and the members marked absent who nonetheless have an amount. None of these blocks the post —
 * a member's neighbour paying on their behalf is ordinary — but every one of them is a
 * transcription error worth looking at twice while the sheet is still in front of you.
 *
 * **After it posts** it shows what the ledger actually took, line by line, with the split the
 * server computed. That split is the one figure a member asks about at the next meeting: how much
 * of what they paid went to penalty rather than to their loan.
 *
 * **What is missing** is a discrepancy that outlives the screen. The permission catalogue has
 * `sync.exception.view` and `sync.exception.resolve`, but the published contract serves no path
 * for either, so a variance raised here has nowhere to be stored, queued, or reviewed. That is
 * stated rather than papered over with a figure that goes nowhere.
 */
export function ReconciliationPanel({
  rows,
  columns,
  amounts,
  objections,
  lines,
  results,
  hasPosted,
}: {
  rows: SheetRow[]
  columns: CollectionColumn[]
  amounts: SheetAmounts
  objections: SheetObjection[]
  lines: PreparedLine[]
  results: Record<string, LineResult>
  hasPosted: boolean
}) {
  const queries = findQueries(rows, columns, amounts)

  return (
    <div className="space-y-4">
      {hasPosted ? <PostedLedgerView lines={lines} results={results} /> : null}

      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle>The sheet against the system</CardTitle>
            <p className="mt-0.5 text-xs text-muted">
              Checked in the browser from balances the server has already sent. Nothing here decides
              whether a posting is allowed — the server does that on every line — but each of these
              is cheaper to look at now than after forty lines have gone.
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {objections.length > 0 ? (
            <Alert tone="danger">
              <TriangleAlert aria-hidden />
              <div className="space-y-1">
                <AlertTitle>
                  {objections.length} line{objections.length === 1 ? '' : 's'} would be refused
                </AlertTitle>
                <AlertDescription>
                  <ul className="space-y-1">
                    {objections.map((objection) => (
                      <li key={`${objection.customerId}:${objection.column}`}>
                        Line {objection.lineNumber}, {objection.memberName} —{' '}
                        {COLUMN_SHORT_LABELS[objection.column]}: {objection.message}{' '}
                        <span className="font-mono text-2xs">{objection.code}</span>
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </div>
            </Alert>
          ) : null}

          {queries.length === 0 ? (
            <Alert tone="success">
              <CheckCircle2 aria-hidden />
              <div className="space-y-1">
                <AlertTitle>Nothing on the sheet contradicts what the system holds</AlertTitle>
                <AlertDescription>
                  Everybody in arrears has an amount against them, and nobody marked absent does.
                </AlertDescription>
              </div>
            </Alert>
          ) : (
            <ul className="space-y-2">
              {queries.map((query) => (
                <li
                  key={`${query.customerId}:${query.kind}`}
                  className="flex gap-2 rounded-card border border-border-subtle bg-surface-muted p-3 text-sm"
                >
                  <FileWarning className="mt-0.5 size-4 shrink-0 text-warning-text" aria-hidden />
                  <div className="min-w-0">
                    <p className="font-medium text-heading">
                      Line {query.lineNumber} · {query.memberName}
                    </p>
                    <p className="text-xs text-body">{query.explanation}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <NotImplemented
        summary="A transcription difference raised here cannot yet be stored, queued, or reviewed. The permission catalogue carries sync.exception.view and sync.exception.resolve, but the published API serves no path behind either, so an exception raised on this screen lives only in this browser and on the paper beside it."
        milestone="the field synchronisation module"
        capabilities={[
          'A declared-total variance recorded against the meeting, not just shown',
          'A queue of open collection exceptions per branch and per officer',
          'Resolution with a reason, countersigned where the amount warrants it',
          'The field application’s own geofence and duplicate-collection exceptions, in the same queue',
        ]}
      />
    </div>
  )
}

interface SheetQuery {
  customerId: string
  lineNumber: number
  memberName: string
  kind: 'owes-nothing-keyed' | 'absent-but-paid'
  explanation: string
}

/**
 * Lines worth a second look. Deliberately not refusals: each of these is ordinary often enough
 * that blocking on it would stop real work, and surprising often enough to be worth naming.
 */
function findQueries(
  rows: SheetRow[],
  columns: CollectionColumn[],
  amounts: SheetAmounts,
): SheetQuery[] {
  const found: SheetQuery[] = []

  for (const row of rows) {
    const total = columns.reduce(
      (sum: Pesewas, column) => sum + (readAmount(amounts, row.customerId, column) ?? 0),
      0,
    )

    const loan = row.cells.LoanRepayment
    const arrears = loan.target?.arrearsMinor ?? 0

    if (arrears > 0 && (readAmount(amounts, row.customerId, 'LoanRepayment') ?? 0) === 0) {
      found.push({
        customerId: row.customerId,
        lineNumber: row.lineNumber,
        memberName: row.fullName,
        kind: 'owes-nothing-keyed',
        explanation: `Overdue on ${loan.target?.reference ?? 'their round'} and nothing is keyed against the repayment column. If they genuinely paid nothing, that is the answer — the register and the arrears will say so.`,
      })
    }

    if (row.attendance === 'Absent' && total > 0) {
      found.push({
        customerId: row.customerId,
        lineNumber: row.lineNumber,
        memberName: row.fullName,
        kind: 'absent-but-paid',
        explanation:
          'Marked absent at this meeting, with money against them. That is ordinary — a neighbour or the leader often pays for someone who could not come — but check it is the right line.',
      })
    }
  }

  return found
}

/** What the ledger actually took, once the batch has run. */
function PostedLedgerView({
  lines,
  results,
}: {
  lines: PreparedLine[]
  results: Record<string, LineResult>
}) {
  const settled = lines.filter((line) => results[line.key] !== undefined)

  if (settled.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>What the ledger took</CardTitle>
          <p className="mt-0.5 text-xs text-muted">
            The split across penalty, fees, interest and principal is the server&apos;s, computed
            against balances read in the same transaction as the posting. It is not a preview and
            it is not recomputed here.
          </p>
        </div>
      </CardHeader>

      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 text-right">Line</TableHead>
              <TableHead>Member</TableHead>
              <TableHead>Column</TableHead>
              <TableHead className="text-right">Keyed</TableHead>
              <TableHead className="text-right">Taken</TableHead>
              <TableHead>Split</TableHead>
              <TableHead>Reference</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {settled.map((line) => {
              const result = results[line.key]
              const posted = result.status === 'posted' ? result : null

              return (
                <TableRow key={line.key}>
                  <TableCell className="numeric text-right text-xs text-muted tabular-nums">
                    {line.lineNumber}
                  </TableCell>
                  <TableCell className="text-sm text-heading">{line.memberName}</TableCell>
                  <TableCell className="text-xs">{COLUMN_SHORT_LABELS[line.column]}</TableCell>
                  <TableCell>
                    <MoneyDisplay value={line.amountMinor} className="text-sm" />
                  </TableCell>
                  <TableCell>
                    {posted ? (
                      <MoneyDisplay
                        value={posted.postedAmountMinor}
                        emphasis={
                          posted.postedAmountMinor === line.amountMinor ? 'normal' : 'strong'
                        }
                        className="text-sm"
                      />
                    ) : (
                      <span className="block text-right text-xs text-danger-text">Refused</span>
                    )}
                  </TableCell>
                  <TableCell className="text-2xs">
                    {posted?.allocation ? (
                      <span className="text-muted">
                        penalty <MoneyDisplay value={posted.allocation.penaltyMinor} symbol={false} className="inline" />
                        {' · '}fee <MoneyDisplay value={posted.allocation.feeMinor} symbol={false} className="inline" />
                        {' · '}interest{' '}
                        <MoneyDisplay value={posted.allocation.interestMinor} symbol={false} className="inline" />
                        {' · '}principal{' '}
                        <MoneyDisplay value={posted.allocation.principalMinor} symbol={false} className="inline" />
                        {posted.cycleClosed ? (
                          <span className="ml-1 font-medium text-success-text">round closed</span>
                        ) : null}
                      </span>
                    ) : result.status === 'refused' ? (
                      <span className="text-danger-text">{resolveErrorMessage(result.error)}</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-2xs">
                    {posted ? posted.reference : '—'}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
